<script setup>
import { computed, inject, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import XlButton from '../xl/XlButton.vue';
import XlTag from '../xl/XlTag.vue';
import PortalJoinButton from './PortalJoinButton.vue';
import PortalSessionPanel from './PortalSessionPanel.vue';
import IconChevronRight from '../icons/IconChevronRight.vue';
import IconRepeat from '../icons/IconRepeat.vue';
import { speakingLook } from '../../helpers/speakerConsent';
import {
  cardMeta, dateTile, kindLabel, longDate, seriesLabel, timeState,
} from '../../helpers/sessionTimes';

/**
 * One session, the same row on Home and on Sessions (XN-4745): the
 * prototype's `.ev`. The title is the toggle, its click stretched over the
 * row; Join sits above it, so joining stays one tap (Hunter, 2026-10-14). The
 * details open in place below, asked for on the first open and kept mounted
 * after, so closing and opening again does not ask twice. The page decides
 * which row is open, one at a time.
 *
 * Inside the Sessions room the call opens in place; without one (Home) it
 * opens on Sessions, which hosts the room. Somebody asked to speak sees the
 * row turn amber with Read the invitation; somebody speaking sees it turn
 * green with Join as a speaker.
 */
const props = defineProps({
  session: { type: Object, required: true },
  now: { type: Date, required: true },
  timeZone: { type: String, required: true },
  open: { type: Boolean, default: false },
});
const emit = defineEmits(['toggle', 'seat', 'gone']);
const room = inject('portalRoom', null);
const router = useRouter();

const tile = computed(() => dateTile(props.session.starts_at, props.timeZone));
const meta = computed(() => cardMeta(props.session, props.now, props.timeZone));
const state = computed(() => timeState(props.session, props.now));
const kind = computed(() => kindLabel(props.session.kind));
const series = computed(() => seriesLabel(props.session.series));
const speak = computed(() => speakingLook(props.session.speaking));
const panelId = computed(() => `session-panel-${props.session.id}`);

const opened = ref(props.open);
watch(() => props.open, (open) => { if (open) opened.value = true; });

const openInvitation = () => router.push(props.session.speaking?.invitation_path ?? `/hub/sessions/${props.session.id}/speaking`);

const joining = ref(false);
async function join() {
  if (!room) {
    router.push({ path: '/hub/sessions', query: { session: props.session.id } });
    return;
  }
  joining.value = true;
  try {
    await room.join(props.session.id);
  } finally {
    joining.value = false;
  }
}
</script>

<template>
  <article
    class="ev"
    :class="{ 'ev--open': open, 'ev--asked': speak?.asked, 'ev--speaking': speak?.speaking }"
    data-testid="session-card"
  >
    <div class="ev__row">
      <div class="ev__date" aria-hidden="true" data-testid="date-tile">
        <span class="ev__m">{{ tile.month }}</span>
        <span class="ev__d">{{ tile.day }}</span>
      </div>
      <div class="ev__in">
        <button
          type="button"
          class="ev__title"
          :aria-expanded="String(open)"
          :aria-controls="opened ? panelId : undefined"
          data-testid="session-toggle"
          @click="emit('toggle', session.id)"
        ><span class="ev__title-text">{{ session.title }}</span></button>
        <span class="ev__meta">{{ meta }}<span class="ev__sr">, {{ longDate(session.starts_at, timeZone) }}</span></span>
        <!-- XN-4745's order: format, repeats, Recorded, Seat saved, then the speaker's state. -->
        <div class="ev__tags">
          <XlTag v-if="state.live" tone="success">Live now</XlTag>
          <XlTag v-if="kind" tone="brand">{{ kind }}</XlTag>
          <XlTag v-if="series"><IconRepeat color="currentColor" size="13" /> {{ series }}</XlTag>
          <XlTag v-if="session.recorded">Recorded</XlTag>
          <XlTag v-if="session.seat" tone="success" data-testid="seat-tag">Seat saved</XlTag>
          <XlTag v-if="speak" :tone="speak.tone" data-testid="speaking-tag">{{ speak.label }}</XlTag>
        </div>
      </div>
      <div class="ev__act">
        <XlButton v-if="speak?.asked" variant="filled" size="sm" data-testid="read-invitation" @click="openInvitation">
          Read the invitation<span class="ev__sr">: {{ session.title }}</span>
        </XlButton>
        <PortalJoinButton
          v-else
          :speaker="Boolean(speak?.speaking)"
          :label="speak?.speaking ? 'Join as a speaker' : 'Join session'"
          :title="session.title"
          :joinable="state.joinable"
          :loading="joining"
          @join="join"
        />
      </div>
      <IconChevronRight
        class="ev__chev"
        :class="{ 'ev__chev--open': open }"
        color="currentColor"
        width="9"
        height="16"
        aria-hidden="true"
      />
    </div>
    <PortalSessionPanel
      v-if="opened"
      v-show="open"
      :id="panelId"
      :session="session"
      :now="now"
      :time-zone="timeZone"
      :joining="joining"
      @join="join"
      @seat="emit('seat', $event)"
      @gone="emit('gone', $event)"
    />
  </article>
</template>

<style scoped>
/* Prototype `.ev` and `.tag` (build 30 Sep 12:40), outlined when open as the staff card is. */
.ev {
  margin-bottom: 10px; background: #fff; border: 1px solid var(--xl-color-border); border-radius: 18px;
  box-shadow: var(--xl-shadow-1); transition: transform 0.18s, border-color 0.18s;
}
.ev:not(.ev--open):hover { transform: translateY(-2px); border-color: var(--xl-color-primary-fixed-dim); }
.ev--open { border-color: var(--xl-color-primary); }
/* Asked to speak: amber, a question still theirs to answer. Speaking: green. */
.ev--asked { border-color: var(--xl-color-warning, #E3B341); box-shadow: inset 4px 0 0 var(--xl-color-warning, #E3B341), var(--xl-shadow-1); }
.ev--speaking { border-color: var(--xl-color-success, #2E8540); box-shadow: inset 4px 0 0 var(--xl-color-success, #2E8540), var(--xl-shadow-1); }
.ev--open.ev--asked, .ev--open.ev--speaking { border-color: var(--xl-color-primary); }
.ev__row { position: relative; display: flex; align-items: center; gap: 16px; padding: 16px 18px; }
.ev__date {
  width: 58px; flex: 0 0 58px; padding: 9px 0; border-radius: 12px; text-align: center;
  background: var(--xl-color-primary-container); color: var(--xl-color-primary); display: flex; flex-direction: column;
  align-self: stretch; justify-content: flex-start;
}
.ev__m { font-size: 10.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
.ev__d { font-size: 21px; font-weight: 600; line-height: 1.1; letter-spacing: -0.02em; }
.ev__in { flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: flex-start; gap: 2px; }
/* The toggle: a real button, two lines at most, its ::after stretched over the row under Join. */
.ev__title {
  padding: 0; border: 0; background: none; cursor: pointer; text-align: left; font: inherit;
  font-size: 15.5px; font-weight: 700; letter-spacing: -0.015em; color: var(--xl-color-on-surface);
}
/* The clamp's overflow sits on the text, not the button that carries ::after. */
.ev__title-text {
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; overflow-wrap: anywhere;
}
.ev__title::after { content: ''; position: absolute; inset: 0; border-radius: 18px; }
.ev__title:focus-visible { outline: none; }
.ev__title:focus-visible::after { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; }
.ev__meta { font-size: 13px; color: var(--xl-color-secondary-text); }
.ev__tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.ev__act { position: relative; z-index: 1; flex: 0 0 auto; display: flex; align-items: center; }
/* Rotated when open, the chevron paints above the title's stretched click area: it takes no clicks, so they reach the toggle. */
.ev__chev { flex: 0 0 auto; color: var(--xl-color-outline); transition: transform 0.18s; pointer-events: none; }
.ev__chev--open { transform: rotate(90deg); }
.ev__sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}

@media (max-width: 600px) {
  .ev__row { flex-wrap: wrap; row-gap: 12px; }
  .ev__in { flex-basis: calc(100% - 74px - 25px); }
  .ev__act { order: 3; margin-left: 74px; }
}

@media (prefers-reduced-motion: reduce) {
  .ev, .ev__chev { transition: none; }
}
</style>
